/* WINGOMART Chair Fit Quiz — deterministic scoring engine.
   Ported from design/secretlab-rebuild/quiz.html with one structural change:
   all RESULT cards (winner panels + runner-up cards) are rendered by Liquid in
   sections/wingomart-page-quiz.liquid with REAL product prices/images/URLs.
   This script only runs the question flow, scores answers, then shows/hides
   the pre-rendered Liquid cards and fills in the per-answer reasons.

   SCORING (mirrors the approved mockup):
   Q3 "Always run hot" is dominant — ERGO MESH is lifted above every rival and
   wins ties (cooling always wins; a PU chair is never recommended to someone
   who always runs hot). Otherwise deterministic tie-break:
   RGB PRO > ERGO MESH > RGB START > MEMORY PRO. Same answers in, same result.

   LOCALISATION:
   Shopify serves ONE copy of this asset to every locale, so no string may be
   hard-coded here. Every user-visible string is read from window.WM_QUIZ_STRINGS,
   emitted by sections/wingomart-page-quiz.liquid from wm.quiz.* locale keys —
   the same bridge pattern as window.variantStrings in layout/theme.liquid.
   The English text stays inline as the second argument to T() so a missing or
   unresolved key degrades to English instead of `undefined`.
*/
(function () {
  'use strict';

  var root = document.querySelector('.wmpg-quiz');
  if (!root) return;

  var stage = document.getElementById('qzStage'),
      progWrap = document.getElementById('qzProgress'),
      progFill = document.getElementById('qzProgFill'),
      progLabel = document.getElementById('qzProgLabel'),
      resultPool = document.getElementById('qzResults'),
      runnerPool = document.getElementById('qzRunnerPool'),
      heroStart = document.getElementById('heroStart'),
      reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!stage || !resultPool || !runnerPool) return;

  /* ---------- locale bridge ----------
     WM_QUIZ_STRINGS is keyed by the full locale key so the JS and the locale
     files can be diffed against each other by eye.

     Values arrive HTML-ready: Shopify escapes any key that does not end in
     `_html`, and every string below is injected with innerHTML (or into an
     attribute inside an innerHTML fragment), where the parser decodes those
     entities again. The two textContent sinks are the exception — they go
     through text() first. */
  var WM_QUIZ_STRINGS = window.WM_QUIZ_STRINGS || {};

  function T(key, fallback) {
    var v = WM_QUIZ_STRINGS[key];
    if (typeof v !== 'string' || v === '' || v.indexOf('Translation missing') === 0) return fallback;
    return v;
  }

  /* Runtime placeholders travel as '[token]' and are substituted here — same
     convention as window.cartStrings / window.quickOrderListStrings. */
  function fill(str, vars) {
    for (var token in vars) {
      if (Object.prototype.hasOwnProperty.call(vars, token)) {
        str = str.split(token).join(vars[token]);
      }
    }
    return str;
  }

  /* textContent does not decode entities, so undo the escaping Shopify applies
     to non-`_html` keys before assigning to a text node. */
  function text(str) {
    return String(str)
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');
  }

  var IMGS = {
    q1: root.getAttribute('data-img-q1') || '',
    q2: root.getAttribute('data-img-q2') || '',
    q3: root.getAttribute('data-img-q3') || '',
    q4: root.getAttribute('data-img-q4') || '',
    q5: root.getAttribute('data-img-q5') || '',
    q6: root.getAttribute('data-img-q6') || ''
  };

  /* Facts pad the reasons list to 3 when few answers point at the winner.
     No prices here — real prices come from the Liquid-rendered cards. */
  var FACTS = {
    rgbpro: [
      T('wm.quiz.result.rgbpro.bullet1', 'Flagship build → lumbar massage cushion included'),
      T('wm.quiz.result.rgbpro.bullet2', 'Retractable footrest → kick back between matches'),
      T('wm.quiz.result.rgbpro.bullet3', 'Built-in RGB lighting → your setup, lit')
    ],
    rgbstart: [
      T('wm.quiz.result.rgbstart.bullet1', 'The best-value build in the lineup'),
      T('wm.quiz.result.rgbstart.bullet2', 'Built-in LED lighting + footrest included'),
      T('wm.quiz.result.rgbstart.bullet3', 'Warranty coverage included, like the flagship')
    ],
    ergomesh: [
      T('wm.quiz.result.ergomesh.bullet1', 'Breathable mesh back → airflow all session'),
      T('wm.quiz.result.ergomesh.bullet2', '3D armrests → dial in your desk fit'),
      T('wm.quiz.result.ergomesh.bullet3', 'Clean, office-friendly silhouette')
    ],
    memorypro: [
      T('wm.quiz.result.memorypro.bullet1', 'Plush memory foam → contour-hugging comfort'),
      T('wm.quiz.result.memorypro.bullet2', '4D armrests → every direction, locked in'),
      T('wm.quiz.result.memorypro.bullet3', '180° recline → nap-grade flat')
    ]
  };

  var QUESTIONS = [
    {
      title: T('wm.quiz.q1.title', 'How many hours a day are you in your chair?'),
      hint: T('wm.quiz.q1.hint', 'Be honest — we’re not judging.'),
      img: IMGS.q1,
      options: [
        { label: T('wm.quiz.q1.opt1.label', 'Under 2'), sub: T('wm.quiz.q1.opt1.sub', 'Quick sessions'), pts: { rgbpro: 1, rgbstart: 2 },
          reasons: { rgbstart: T('wm.quiz.q1.opt1.reason.rgbstart_html', '<b>Light daily use</b> → full support without paying flagship money'), rgbpro: T('wm.quiz.q1.opt1.reason.rgbpro_html', '<b>Short sessions</b> → cushioned comfort from minute one') } },
        { label: T('wm.quiz.q1.opt2.label', '2–5'), sub: T('wm.quiz.q1.opt2.sub', 'Solid daily stints'), pts: { rgbpro: 2, rgbstart: 1 },
          reasons: { rgbpro: T('wm.quiz.q1.opt2.reason.rgbpro_html', '<b>2–5 hours a day</b> → all-round adjustability for mixed work-and-play days'), rgbstart: T('wm.quiz.q1.opt2.reason.rgbstart_html', '<b>Regular daily use</b> → dependable everyday ergonomics') } },
        { label: T('wm.quiz.q1.opt3.label', '6–8'), sub: T('wm.quiz.q1.opt3.sub', 'The long shift'), pts: { rgbpro: 3, ergomesh: 2, memorypro: 1 },
          reasons: { rgbpro: T('wm.quiz.q1.opt3.reason.rgbpro_html', '<b>6–8 hour days</b> → flagship-grade lumbar support built for long shifts'), ergomesh: T('wm.quiz.q1.opt3.reason.ergomesh_html', '<b>6–8 hour days</b> → a breathable build that stays comfortable late'), memorypro: T('wm.quiz.q1.opt3.reason.memorypro_html', '<b>Long days</b> → dense memory foam that never bottoms out') } },
        { label: T('wm.quiz.q1.opt4.label', 'It IS my home'), sub: T('wm.quiz.q1.opt4.sub', 'Chair-based lifestyle'), pts: { rgbpro: 1, ergomesh: 2, memorypro: 3 },
          reasons: { memorypro: T('wm.quiz.q1.opt4.reason.memorypro_html', '<b>You practically live in it</b> → plush memory foam and a 180° recline'), ergomesh: T('wm.quiz.q1.opt4.reason.ergomesh_html', '<b>Marathon sessions</b> → mesh keeps its shape and airflow all day'), rgbpro: T('wm.quiz.q1.opt4.reason.rgbpro_html', '<b>All-day sitting</b> → the full adjustability suite earns its keep') } }
      ]
    },
    {
      title: T('wm.quiz.q2.title', 'Pick your seat feel:'),
      hint: T('wm.quiz.q2.hint', 'Trust your gut — first instinct is usually right.'),
      img: IMGS.q2,
      options: [
        { label: T('wm.quiz.q2.opt1.label', 'Cushioned PU leather'), sub: T('wm.quiz.q2.opt1.sub', 'Classic racing-chair feel'), pts: { rgbpro: 3, rgbstart: 2 },
          reasons: { rgbpro: T('wm.quiz.q2.opt1.reason.rgbpro_html', '<b>You like cushioned PU leather</b> → premium quilted PU seat and back'), rgbstart: T('wm.quiz.q2.opt1.reason.rgbstart_html', '<b>Cushioned PU leather</b> → the same seat feel at the entry price') } },
        { label: T('wm.quiz.q2.opt2.label', 'Breathable mesh'), sub: T('wm.quiz.q2.opt2.sub', 'Airy and supportive'), pts: { ergomesh: 4 },
          reasons: { ergomesh: T('wm.quiz.q2.opt2.reason.ergomesh_html', '<b>You picked breathable mesh</b> → full mesh back that moves air, not sweat') } },
        { label: T('wm.quiz.q2.opt3.label', 'Plush memory foam'), sub: T('wm.quiz.q2.opt3.sub', 'Sink-in comfort'), pts: { memorypro: 4 },
          reasons: { memorypro: T('wm.quiz.q2.opt3.reason.memorypro_html', '<b>You picked plush memory foam</b> → contour-hugging foam in seat and back') } },
        { label: T('wm.quiz.q2.opt4.label', 'Not sure'), sub: T('wm.quiz.q2.opt4.sub', 'Surprise me'), pts: { rgbpro: 1 },
          reasons: { rgbpro: T('wm.quiz.q2.opt4.reason.rgbpro_html', '<b>Not sure on feel</b> → our most-loved all-rounder is the safe bet') } }
      ]
    },
    {
      title: T('wm.quiz.q3.title', 'Do you run hot while gaming?'),
      hint: T('wm.quiz.q3.hint', 'Sweaty-back honesty hour.'),
      img: IMGS.q3,
      options: [
        { label: T('wm.quiz.q3.opt1.label', 'Always'), sub: T('wm.quiz.q3.opt1.sub', 'Human space heater'), pts: { ergomesh: 6 },
          reasons: { ergomesh: T('wm.quiz.q3.opt1.reason.ergomesh_html', '<b>You run hot</b> → breathable mesh back keeps you cool for the whole session') } },
        { label: T('wm.quiz.q3.opt2.label', 'Sometimes'), sub: T('wm.quiz.q3.opt2.sub', 'Ranked games only'), pts: { rgbpro: 1, ergomesh: 2 },
          reasons: { ergomesh: T('wm.quiz.q3.opt2.reason.ergomesh_html', '<b>You sometimes run hot</b> → airflow-friendly mesh takes the edge off'), rgbpro: T('wm.quiz.q3.opt2.reason.rgbpro_html', '<b>Occasional warm sessions</b> → the PU build breathes better than you’d think') } },
        { label: T('wm.quiz.q3.opt3.label', 'Rarely'), sub: T('wm.quiz.q3.opt3.sub', 'Cool as ice'), pts: { rgbpro: 1, memorypro: 1 },
          reasons: { rgbpro: T('wm.quiz.q3.opt3.reason.rgbpro_html', '<b>You rarely run hot</b> → cushioned PU comfort with zero trade-off'), memorypro: T('wm.quiz.q3.opt3.reason.memorypro_html', '<b>You stay cool</b> → free to go maximum plush') } }
      ]
    },
    {
      title: T('wm.quiz.q4.title', 'Footrest — need one built in?'),
      hint: T('wm.quiz.q4.hint', 'The kick-back-between-queues question.'),
      img: IMGS.q4,
      options: [
        { label: T('wm.quiz.q4.opt1.label', 'Absolutely'), sub: T('wm.quiz.q4.opt1.sub', 'Feet up or bust'), pts: { rgbpro: 3, rgbstart: 3 },
          reasons: { rgbpro: T('wm.quiz.q4.opt1.reason.rgbpro_html', '<b>Footrest is a must</b> → built-in retractable footrest, ready when you are'), rgbstart: T('wm.quiz.q4.opt1.reason.rgbstart_html', '<b>Footrest is a must</b> → built in, even at the entry price') } },
        { label: T('wm.quiz.q4.opt2.label', 'Nice to have'), sub: T('wm.quiz.q4.opt2.sub', 'Wouldn’t say no'), pts: { rgbpro: 2, rgbstart: 1 },
          reasons: { rgbpro: T('wm.quiz.q4.opt2.reason.rgbpro_html', '<b>Footrest nice-to-have</b> → it’s built in; use it when the mood hits'), rgbstart: T('wm.quiz.q4.opt2.reason.rgbstart_html', '<b>Footrest nice-to-have</b> → included at no extra cost') } },
        { label: T('wm.quiz.q4.opt3.label', 'No'), sub: T('wm.quiz.q4.opt3.sub', 'Feet on the floor'), pts: { ergomesh: 2, memorypro: 2 },
          reasons: { ergomesh: T('wm.quiz.q4.opt3.reason.ergomesh_html', '<b>No footrest needed</b> → clean, minimal frame with nothing extra'), memorypro: T('wm.quiz.q4.opt3.reason.memorypro_html', '<b>No footrest needed</b> → the 180° recline does the relaxing instead') } }
      ]
    },
    {
      title: T('wm.quiz.q5.title', 'Lighting built into the chair itself?'),
      hint: T('wm.quiz.q5.hint', 'Yes, the glow is built into the chair.'),
      img: IMGS.q5,
      options: [
        { label: T('wm.quiz.q5.opt1.label', 'Yes, light it up'), sub: T('wm.quiz.q5.opt1.sub', 'Full glow'), pts: { rgbpro: 3, rgbstart: 2 },
          reasons: { rgbpro: T('wm.quiz.q5.opt1.reason.rgbpro_html', '<b>Light it up</b> → full RGB edge lighting built into the chair'), rgbstart: T('wm.quiz.q5.opt1.reason.rgbstart_html', '<b>Glow on a budget</b> → built-in LED lighting at the entry price') } },
        { label: T('wm.quiz.q5.opt2.label', 'Subtle is fine'), sub: T('wm.quiz.q5.opt2.sub', 'A hint of glow'), pts: { rgbpro: 1, rgbstart: 2 },
          reasons: { rgbstart: T('wm.quiz.q5.opt2.reason.rgbstart_html', '<b>Subtle glow</b> → LED lighting you can switch off entirely'), rgbpro: T('wm.quiz.q5.opt2.reason.rgbpro_html', '<b>Subtle glow</b> → dimmable RGB that behaves itself') } },
        { label: T('wm.quiz.q5.opt3.label', 'No lights'), sub: T('wm.quiz.q5.opt3.sub', 'Stealth mode'), pts: { ergomesh: 2, memorypro: 2 },
          reasons: { ergomesh: T('wm.quiz.q5.opt3.reason.ergomesh_html', '<b>No lights</b> → clean, office-friendly look — zero glow'), memorypro: T('wm.quiz.q5.opt3.reason.memorypro_html', '<b>No lights</b> → understated, executive-style build') } }
      ]
    },
    {
      title: T('wm.quiz.q6.title', 'Budget comfort zone?'),
      hint: T('wm.quiz.q6.hint', 'Real store prices show up with your result.'),
      img: IMGS.q6,
      options: [
        { label: T('wm.quiz.q6.opt1.label', 'Under $200'), sub: T('wm.quiz.q6.opt1.sub', 'Smart spend'), pts: { rgbstart: 4 },
          reasons: { rgbstart: T('wm.quiz.q6.opt1.reason.rgbstart_html', '<b>Under $200</b> → the best-value build in the lineup, lighting and footrest included') } },
        { label: T('wm.quiz.q6.opt2.label', '$200–$260'), sub: T('wm.quiz.q6.opt2.sub', 'The sweet spot'), pts: { rgbpro: 3, ergomesh: 1 },
          reasons: { rgbpro: T('wm.quiz.q6.opt2.reason.rgbpro_html', '<b>$200–$260 zone</b> → the flagship lands right in your range'), ergomesh: T('wm.quiz.q6.opt2.reason.ergomesh_html', '<b>$200–$260 zone</b> → the mesh build sits at the top of it') } },
        { label: T('wm.quiz.q6.opt3.label', 'Whatever it takes'), sub: T('wm.quiz.q6.opt3.sub', 'Comfort is priceless'), pts: { rgbpro: 1, memorypro: 3 },
          reasons: { memorypro: T('wm.quiz.q6.opt3.reason.memorypro_html', '<b>No budget cap</b> → our most premium build, no compromises'), rgbpro: T('wm.quiz.q6.opt3.reason.rgbpro_html', '<b>Comfort first</b> → the flagship never disappoints') } }
      ]
    }
  ];

  var state = { screen: 'intro', q: 0, answers: [] };
  var introHTML = stage.innerHTML; /* intro panel is server-rendered by Liquid */

  /* ---------- scoring ---------- */
  function score(answers) {
    var totals = { rgbpro: 0, rgbstart: 0, ergomesh: 0, memorypro: 0 };
    answers.forEach(function (optIdx, qIdx) {
      var pts = QUESTIONS[qIdx].options[optIdx].pts;
      for (var k in pts) totals[k] += pts[k];
    });
    if (answers[2] === 0) { /* "Always" hot → cooling always wins */
      var topRival = Math.max(totals.rgbpro, totals.rgbstart, totals.memorypro);
      if (totals.ergomesh <= topRival) totals.ergomesh = topRival + 1;
    }
    var priority = (answers[2] === 0)
      ? ['ergomesh', 'rgbpro', 'rgbstart', 'memorypro']
      : ['rgbpro', 'ergomesh', 'rgbstart', 'memorypro'];
    var ranked = Object.keys(totals).sort(function (a, b) {
      if (totals[b] !== totals[a]) return totals[b] - totals[a];
      return priority.indexOf(a) - priority.indexOf(b);
    });
    return { totals: totals, ranked: ranked };
  }

  function reasonsFor(chairId, answers) {
    var out = [];
    answers.forEach(function (optIdx, qIdx) {
      var r = QUESTIONS[qIdx].options[optIdx].reasons;
      if (r && r[chairId]) out.push(r[chairId]);
    });
    var facts = (FACTS[chairId] || []).slice();
    while (out.length < 3 && facts.length) out.push(facts.shift());
    return out.slice(0, 3);
  }

  /* ---------- rendering ---------- */
  function questionHTML(qIdx) {
    var q = QUESTIONS[qIdx];
    var opts = q.options.map(function (o, i) {
      var picked = state.answers[qIdx] === i ? ' picked' : '';
      return '<button type="button" class="qz-opt' + picked + '" data-act="answer" data-opt="' + i + '">' +
        '<span class="key" aria-hidden="true">' + (i + 1) + '</span>' +
        '<span><span>' + o.label + '</span>' + (o.sub ? '<span class="opt-sub">' + o.sub + '</span>' : '') + '</span>' +
      '</button>';
    }).join('');
    var step = fill(T('wm.quiz.ui.step', 'Question [current] of [total]'),
      { '[current]': qIdx + 1, '[total]': QUESTIONS.length });
    var kbdHint = fill(T('wm.quiz.ui.kbd_hint', 'Tip: press 1–[max] to answer'),
      { '[max]': q.options.length });
    return '<div class="qz-panel qz-q">' +
      '<div class="q-media" role="img" aria-label="" style="background-image:url(\'' + q.img + '\')"></div>' +
      '<div class="q-body">' +
        '<div class="q-step">' + step + '</div>' +
        '<h2 tabindex="-1">' + q.title + '</h2>' +
        '<p class="q-hint">' + q.hint + '</p>' +
        '<div class="qz-opts" role="group" aria-label="' + T('wm.quiz.ui.answers_aria', 'Answers') + '">' + opts + '</div>' +
        '<div class="q-foot">' +
          '<button type="button" class="qz-back" data-act="back"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>' + T('wm.quiz.ui.back', 'Back') + '</button>' +
          '<span class="qz-kbd-hint">' + kbdHint + '</span>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function buildResult() {
    var r = score(state.answers),
        winId = r.ranked[0],
        maxScore = r.totals[winId];
    var srcPanel = resultPool.querySelector('[data-chair="' + winId + '"]');
    if (!srcPanel) return document.createTextNode('');
    var panel = srcPanel.cloneNode(true);
    panel.removeAttribute('hidden');
    /* reasons */
    var reasonsBox = panel.querySelector('.qz-reasons');
    if (reasonsBox) {
      reasonsBox.innerHTML = reasonsFor(winId, state.answers).map(function (t) {
        return '<div class="qz-reason"><span class="tick" aria-hidden="true">&#10003;</span><span>' + t + '</span></div>';
      }).join('');
    }
    /* runners-up: top 2 non-winners, cloned from the Liquid pool */
    var row = panel.querySelector('.qz-runner-row');
    if (row) {
      row.innerHTML = '';
      r.ranked.slice(1, 3).forEach(function (id) {
        var src = runnerPool.querySelector('[data-chair="' + id + '"]');
        if (!src) return;
        var card = src.cloneNode(true);
        card.removeAttribute('hidden');
        var pct = maxScore ? Math.round(r.totals[id] / maxScore * 100) : 0;
        var m = card.querySelector('[data-match]');
        if (m) m.textContent = fill(text(T('wm.quiz.ui.match', '[pct]% match')), { '[pct]': pct });
        row.appendChild(card);
      });
    }
    return panel;
  }

  function updateProgress() {
    if (!progWrap) return;
    if (state.screen === 'question') {
      progWrap.classList.add('on');
      var n = state.q + 1;
      progWrap.setAttribute('aria-valuenow', n);
      if (progLabel) progLabel.textContent = fill(text(T('wm.quiz.ui.step', 'Question [current] of [total]')),
        { '[current]': n, '[total]': QUESTIONS.length });
      if (progFill) progFill.style.width = (n / QUESTIONS.length * 100) + '%';
    } else {
      progWrap.classList.remove('on');
    }
  }

  function render(dir) {
    updateProgress();
    var old = stage.firstElementChild;
    var apply = function () {
      if (state.screen === 'intro') {
        stage.innerHTML = introHTML;
      } else if (state.screen === 'result') {
        stage.innerHTML = '';
        stage.appendChild(buildResult());
      } else {
        stage.innerHTML = questionHTML(state.q);
      }
      var panel = stage.firstElementChild;
      if (panel && !reduceMotion && dir !== 0) panel.classList.add(dir < 0 ? 'anim-in-back' : 'anim-in');
      var h = panel && panel.querySelector('h2');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    };
    if (old && !reduceMotion && dir !== 0) {
      old.classList.add(dir < 0 ? 'anim-out-back' : 'anim-out');
      setTimeout(apply, 200);
    } else {
      apply();
    }
  }

  function scrollToQuiz() {
    var el = document.getElementById('quiz');
    if (el) el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* ---------- events ---------- */
  stage.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-act]');
    if (!btn) return;
    var act = btn.getAttribute('data-act');
    if (act === 'start') {
      state.screen = 'question'; state.q = 0; state.answers = [];
      render(1);
    } else if (act === 'answer') {
      state.answers[state.q] = parseInt(btn.getAttribute('data-opt'), 10);
      if (state.q < QUESTIONS.length - 1) { state.q++; render(1); }
      else { state.screen = 'result'; render(1); }
    } else if (act === 'back') {
      if (state.q > 0) { state.q--; render(-1); }
      else { state.screen = 'intro'; render(-1); }
    } else if (act === 'retake') {
      state.screen = 'question'; state.q = 0; state.answers = [];
      render(-1);
      scrollToQuiz();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (state.screen !== 'question') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    var tag = (document.activeElement && document.activeElement.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    var n = parseInt(e.key, 10);
    if (!isNaN(n) && n >= 1 && n <= QUESTIONS[state.q].options.length) {
      var opt = stage.querySelector('.qz-opt[data-opt="' + (n - 1) + '"]');
      if (opt) opt.click();
    }
  });

  if (heroStart) {
    heroStart.addEventListener('click', function (e) {
      e.preventDefault();
      if (state.screen === 'intro') { state.screen = 'question'; state.q = 0; state.answers = []; render(0); }
      scrollToQuiz();
    });
  }
})();
